import React, { useState, useEffect } from 'react';
import EmptyState from '../../components/EmptyState';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  IconButton,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  TextField,
  InputAdornment,
  Tooltip,
  Grid,
  Card,
  CardContent,
  Collapse,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Alert
} from '@mui/material';
import {
  ArrowBack,
  Add,
  Edit,
  Delete,
  Search,
  Person,
  Phone,
  Bloodtype,
  School,
  ContactPhone,
  ExpandMore,
  ExpandLess,
  CalendarMonth,
  Home,
  Badge,
  LocalHospital,
  Email,
  FolderOpen as FolderIcon,
} from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { principalAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';

const DOC_TYPE_LABELS = {
  birth_certificate:    'Birth Certificate',
  transfer_certificate: 'Transfer Certificate',
  aadhaar:              'Aadhaar',
  photo:                'Photo',
  report_card:          'Report Card',
  other:                'Other',
};

const Students = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const highlightId = searchParams.get('highlight');
  const { user, logout } = useAuth();
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, student: null });
  const [deleteLoading, setDeleteLoading] = useState(false);
  // compliance: Map<student_id, { is_complete, total_required, total_matched, missing_types }>
  const [complianceMap, setComplianceMap] = useState({});

  const handleEdit = (e, studentId) => {
    e.stopPropagation();
    navigate(`/principal/students/edit/${studentId}`);
  };

  const handleDeleteClick = (e, student) => {
    e.stopPropagation();
    setDeleteDialog({ open: true, student });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteDialog.student) return;
    setDeleteLoading(true);
    try {
      await principalAPI.deleteStudent(deleteDialog.student.id);
      setStudents(students.filter(s => s.id !== deleteDialog.student.id));
      if (expandedId === deleteDialog.student.id) setExpandedId(null);
      setDeleteDialog({ open: false, student: null });
    } catch (error) {
      console.error('Error deleting student:', error);
      alert('Failed to delete student');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialog({ open: false, student: null });
  };

  const toggleExpand = (studentId) => {
    setExpandedId(expandedId === studentId ? null : studentId);
  };

  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      try {
        const params = selectedClass ? { class_id: selectedClass } : {};
        const [clsRes, studRes] = await Promise.all([
          principalAPI.getClasses(),
          principalAPI.getStudents(params),
        ]);
        if (isMounted) {
          setClasses(clsRes.data.classes || []);
          setStudents(studRes.data.students || []);
        }
        // Load compliance data in background (non-blocking)
        principalAPI.documents.compliance().then(({ data }) => {
          if (!isMounted) return;
          const map = {};
          for (const s of (data.students || [])) {
            map[s.student_id] = {
              is_complete:    s.is_complete,
              total_required: s.total_required,
              total_matched:  s.total_matched,
              missing_types:  s.missing_types,
            };
          }
          setComplianceMap(map);
        }).catch(() => {});
      } catch (error) {
        console.error('Error fetching students or classes:', error);
        if (isMounted) {
          setStudents([]);
          setClasses([]);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, [selectedClass]);

  useEffect(() => {
    let isMounted = true;
    const fetchStudents = async () => {
      try {
        setLoading(true);
        const params = selectedClass ? { class_id: selectedClass } : {};
        const response = await principalAPI.getStudents(params);
        if (isMounted) setStudents(response.data.students || []);
      } catch (error) {
        console.error('Error fetching students for class filter:', error);
        if (isMounted) setStudents([]);
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    fetchStudents();
    return () => { isMounted = false; };
  }, [selectedClass]);

  useEffect(() => {
    if (!highlightId || loading || students.length === 0) return;
    setExpandedId(highlightId);
    const el = document.getElementById(`student-${highlightId}`);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('highlight');
      return next;
    }, { replace: true });
  }, [highlightId, loading, students, setSearchParams]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  const filteredStudents = students.filter(student => {
    const searchLower = searchTerm.toLowerCase();
    return (
      student.name?.toLowerCase().includes(searchLower) ||
      student.admission_number?.toLowerCase().includes(searchLower) ||
      student.phone?.toLowerCase().includes(searchLower) ||
      student.father_name?.toLowerCase().includes(searchLower) ||
      student.mother_name?.toLowerCase().includes(searchLower)
    );
  });

  const DetailItem = ({ icon, label, value }) => {
    if (!value) return null;
    return (
      <Box display="flex" alignItems="center" gap={1} py={0.5}>
        {icon}
        <Typography variant="body2" color="text.secondary" sx={{ minWidth: 120 }}>
          {label}:
        </Typography>
        <Typography variant="body2" fontWeight={500}>
          {value}
        </Typography>
      </Box>
    );
  };

  return (
    <Container maxWidth="xl" sx={{ mt: { xs: 2, md: 4 }, mb: 4, px: { xs: 1.5, sm: 3 } }}>
      {/* Header */}
      <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} gap={2} mb={3}>
        <Typography variant="h4" fontWeight="bold" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
          Manage Students
        </Typography>
        <Box display="flex" gap={1} flexWrap="wrap">
          <Button
            variant="outlined"
            startIcon={<ArrowBack />}
            onClick={() => navigate('/principal/dashboard')}
          >
            Back to Dashboard
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => navigate('/principal/students/new')}
          >
            Add Student
          </Button>
        </Box>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} sm={6} md={3}>
          <Card
            elevation={0}
            sx={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
              '&:hover': {
                transform: 'translateY(-8px)',
                boxShadow: '0 20px 25px -5px rgba(102, 126, 234, 0.3)',
              },
            }}
          >
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography sx={{ color: 'rgba(255,255,255,0.9)', fontSize: '0.875rem', mb: 1 }} fontWeight={500}>
                    Total Students
                  </Typography>
                  <Typography variant="h3" fontWeight="bold">
                    {students.length}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    backgroundColor: 'rgba(255,255,255,0.2)',
                    borderRadius: '50%',
                    p: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Person sx={{ fontSize: 40, color: 'white' }} />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card
            elevation={0}
            sx={{
              background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
              color: 'white',
              transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
              '&:hover': {
                transform: 'translateY(-8px)',
                boxShadow: '0 20px 25px -5px rgba(67, 233, 123, 0.3)',
              },
            }}
          >
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography sx={{ color: 'rgba(255,255,255,0.9)', fontSize: '0.875rem', mb: 1 }} fontWeight={500}>
                    Assigned to Class
                  </Typography>
                  <Typography variant="h3" fontWeight="bold">
                    {students.filter(s => s.class_id).length}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    backgroundColor: 'rgba(255,255,255,0.2)',
                    borderRadius: '50%',
                    p: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <School sx={{ fontSize: 40, color: 'white' }} />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card
            elevation={0}
            sx={{
              background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
              color: 'white',
              transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
              '&:hover': {
                transform: 'translateY(-8px)',
                boxShadow: '0 20px 25px -5px rgba(79, 172, 254, 0.3)',
              },
            }}
          >
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography sx={{ color: 'rgba(255,255,255,0.9)', fontSize: '0.875rem', mb: 1 }} fontWeight={500}>
                    With Parent Info
                  </Typography>
                  <Typography variant="h3" fontWeight="bold">
                    {students.filter(s => s.father_name || s.mother_name).length}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    backgroundColor: 'rgba(255,255,255,0.2)',
                    borderRadius: '50%',
                    p: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <ContactPhone sx={{ fontSize: 40, color: 'white' }} />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card
            elevation={0}
            sx={{
              background: 'linear-gradient(135deg, #f5576c 0%, #e91e63 100%)',
              color: 'white',
              transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
              '&:hover': {
                transform: 'translateY(-8px)',
                boxShadow: '0 20px 25px -5px rgba(245, 87, 108, 0.3)',
              },
            }}
          >
            <CardContent>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography sx={{ color: 'rgba(255,255,255,0.9)', fontSize: '0.875rem', mb: 1 }} fontWeight={500}>
                    With Blood Group
                  </Typography>
                  <Typography variant="h3" fontWeight="bold">
                    {students.filter(s => s.blood_group).length}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    backgroundColor: 'rgba(255,255,255,0.2)',
                    borderRadius: '50%',
                    p: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Bloodtype sx={{ fontSize: 40, color: 'white' }} />
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters and Search */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search by name, admission number, phone, or parent name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth size="small">
              <InputLabel id="class-filter-label">Filter by Class</InputLabel>
              <Select
                labelId="class-filter-label"
                value={selectedClass}
                label="Filter by Class"
                onChange={(e) => setSelectedClass(e.target.value)}
              >
                <MenuItem value="">All Classes</MenuItem>
                {classes.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {`${c.name} ${c.grade || ''} ${c.section || ''}`.trim()}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Students List */}
      {filteredStudents.length === 0 ? (
        <EmptyState
          icon={Person}
          variant={searchTerm ? 'no-results' : 'empty'}
          title={searchTerm ? 'No students found matching your search' : 'No students found'}
          description={searchTerm ? 'Try adjusting your search terms' : 'Click "Add Student" to create your first student'}
          actionLabel={!searchTerm ? 'Add Student' : undefined}
          onAction={!searchTerm ? () => navigate('/principal/students/new') : undefined}
        />
      ) : (
        <Box display="flex" flexDirection="column" gap={1.5}>
          {filteredStudents.map((student) => {
            const isExpanded = expandedId === student.id;
            return (
              <Paper
                key={student.id}
                id={`student-${student.id}`}
                elevation={isExpanded ? 4 : 1}
                sx={{
                  overflow: 'hidden',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  border: isExpanded ? '1px solid' : '1px solid transparent',
                  borderColor: isExpanded ? 'primary.main' : 'transparent',
                  '&:hover': {
                    boxShadow: 3,
                  },
                }}
                onClick={() => toggleExpand(student.id)}
              >
                {/* Student Summary Row */}
                <Box
                  display="flex"
                  alignItems="center"
                  px={2.5}
                  py={1.5}
                  gap={2}
                >
                  {/* Expand Icon */}
                  <Box sx={{ color: 'text.secondary' }}>
                    {isExpanded ? <ExpandLess /> : <ExpandMore />}
                  </Box>

                  {/* Avatar */}
                  <Box
                    sx={{
                      width: 42,
                      height: 42,
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <Typography variant="body1" fontWeight="bold" color="white">
                      {student.name?.charAt(0)?.toUpperCase() || '?'}
                    </Typography>
                  </Box>

                  {/* Name & Admission */}
                  <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                    <Typography variant="body1" fontWeight={600} noWrap>
                      {student.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" fontFamily="monospace">
                      {student.admission_number}
                    </Typography>
                  </Box>

                  {/* Class Chip */}
                  <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                    {student.class_id ? (
                      <Chip
                        label={student.class_name || 'Class'}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    ) : (
                      <Chip label="No Class" size="small" variant="outlined" />
                    )}
                  </Box>

                  {/* Phone */}
                  <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 0.5 }}>
                    <Phone sx={{ fontSize: 16, color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary">
                      {student.phone || '—'}
                    </Typography>
                  </Box>

                  {/* Compliance badge */}
                  {(() => {
                    const c = complianceMap[student.id];
                    if (!c || c.total_required === 0) return null;
                    if (c.is_complete) {
                      return (
                        <Chip
                          size="small"
                          label="Docs Complete"
                          sx={{ bgcolor: '#e8f5e9', color: '#2e7d32', fontWeight: 600, fontSize: '0.7rem' }}
                        />
                      );
                    }
                    return (
                      <Tooltip title={`Missing: ${c.missing_types.map(t => DOC_TYPE_LABELS[t] || t).join(', ')}`}>
                        <Chip
                          size="small"
                          label={`Docs ${c.total_matched}/${c.total_required}`}
                          sx={{ bgcolor: c.total_matched === 0 ? '#ffebee' : '#fff3e0', color: c.total_matched === 0 ? '#c62828' : '#e65100', fontWeight: 600, fontSize: '0.7rem', cursor: 'default' }}
                        />
                      </Tooltip>
                    );
                  })()}

                  {/* Action Buttons */}
                  <Box display="flex" gap={0.5} sx={{ flexShrink: 0 }}>
                    <Tooltip title="View Documents">
                      <IconButton
                        size="small"
                        color="info"
                        onClick={(e) => { e.stopPropagation(); navigate(`/principal/students/${student.id}/documents`); }}
                        sx={{ bgcolor: '#e3f2fd', '&:hover': { bgcolor: '#bbdefb' } }}
                      >
                        <FolderIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit Student">
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={(e) => handleEdit(e, student.id)}
                        sx={{
                          bgcolor: 'primary.50',
                          '&:hover': { bgcolor: 'primary.100' },
                        }}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete Student">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={(e) => handleDeleteClick(e, student)}
                        sx={{
                          bgcolor: 'error.50',
                          '&:hover': { bgcolor: 'error.100' },
                        }}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>

                {/* Expanded Details */}
                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                  <Divider />
                  <Box px={3} py={2.5} sx={{ bgcolor: 'grey.50' }}>
                    <Grid container spacing={3}>
                      {/* Student Information */}
                      <Grid item xs={12} md={4}>
                        <Typography variant="subtitle2" color="primary" fontWeight={700} gutterBottom>
                          Student Information
                        </Typography>
                        <DetailItem
                          icon={<Person sx={{ fontSize: 18, color: 'text.secondary' }} />}
                          label="Full Name"
                          value={student.name}
                        />
                        <DetailItem
                          icon={<Badge sx={{ fontSize: 18, color: 'text.secondary' }} />}
                          label="Admission No"
                          value={student.admission_number}
                        />
                        <DetailItem
                          icon={<Phone sx={{ fontSize: 18, color: 'text.secondary' }} />}
                          label="Phone"
                          value={student.phone}
                        />
                        <DetailItem
                          icon={<CalendarMonth sx={{ fontSize: 18, color: 'text.secondary' }} />}
                          label="Date of Birth"
                          value={student.date_of_birth ? new Date(student.date_of_birth).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : null}
                        />
                        <DetailItem
                          icon={<Bloodtype sx={{ fontSize: 18, color: 'text.secondary' }} />}
                          label="Blood Group"
                          value={student.blood_group}
                        />
                        <DetailItem
                          icon={<School sx={{ fontSize: 18, color: 'text.secondary' }} />}
                          label="Class"
                          value={student.class_name || (student.class_id ? 'Assigned' : 'Not Assigned')}
                        />
                      </Grid>

                      {/* Parent/Guardian Information */}
                      <Grid item xs={12} md={4}>
                        <Typography variant="subtitle2" color="primary" fontWeight={700} gutterBottom>
                          Parent / Guardian
                        </Typography>
                        <DetailItem
                          icon={<Person sx={{ fontSize: 18, color: 'text.secondary' }} />}
                          label="Father Name"
                          value={student.father_name}
                        />
                        <DetailItem
                          icon={<Phone sx={{ fontSize: 18, color: 'text.secondary' }} />}
                          label="Father Phone"
                          value={student.father_phone}
                        />
                        <DetailItem
                          icon={<Person sx={{ fontSize: 18, color: 'text.secondary' }} />}
                          label="Mother Name"
                          value={student.mother_name}
                        />
                        <DetailItem
                          icon={<Phone sx={{ fontSize: 18, color: 'text.secondary' }} />}
                          label="Mother Phone"
                          value={student.mother_phone}
                        />
                        <DetailItem
                          icon={<LocalHospital sx={{ fontSize: 18, color: 'text.secondary' }} />}
                          label="Emergency"
                          value={student.emergency_phone}
                        />
                        <DetailItem
                          icon={<Email sx={{ fontSize: 18, color: 'text.secondary' }} />}
                          label="Parent Email"
                          value={student.parent_email || student.email}
                        />
                      </Grid>

                      {/* Additional Info */}
                      <Grid item xs={12} md={4}>
                        <Typography variant="subtitle2" color="primary" fontWeight={700} gutterBottom>
                          Additional Details
                        </Typography>
                        <DetailItem
                          icon={<Home sx={{ fontSize: 18, color: 'text.secondary' }} />}
                          label="Address"
                          value={student.address}
                        />
                        {!student.father_name && !student.mother_name && !student.emergency_phone && !student.address && !student.date_of_birth && !student.blood_group && (
                          <Alert severity="info" variant="outlined" sx={{ mt: 1 }}>
                            No additional details available. Click Edit to add more information.
                          </Alert>
                        )}
                      </Grid>
                    </Grid>
                  </Box>
                </Collapse>
              </Paper>
            );
          })}
        </Box>
      )}

      {/* Results Summary */}
      {filteredStudents.length > 0 && (
        <Box mt={2} display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="body2" color="text.secondary">
            Showing {filteredStudents.length} of {students.length} students
          </Typography>
        </Box>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={handleDeleteCancel}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ color: 'error.main', fontWeight: 600 }}>
          Delete Student
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete <strong>{deleteDialog.student?.name}</strong>
            {deleteDialog.student?.admission_number && (
              <> (Admission No: <strong>{deleteDialog.student.admission_number}</strong>)</>
            )}
            ? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleDeleteCancel} disabled={deleteLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleteLoading}
            startIcon={deleteLoading ? <CircularProgress size={16} /> : <Delete />}
          >
            {deleteLoading ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default React.memo(Students);
